import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Card,
  GET_ALL_FORM_VERSIONS_FOR_CURRENT_USER,
  GET_CHARTABLE_FIELDS,
  GET_DASHBOARDS,
  GET_MY_BUILDER_PREFERENCE,
  GET_SAVED_CHARTS,
  GET_SURVEY_HIGHLIGHTS,
  GET_SURVEY_ITEM_RANKING,
  PIN_CHART,
  RUN_CHART_QUERIES,
  SAVE_BUILDER_PREFERENCE,
  UNPIN_CHART,
  useAlert,
  DateRangePicker,
} from '@employ-engage/common-components';
import {
  ChartResult,
  DateRangeSpec,
  isCompleteRange,
  CompareMode,
} from '@employ-engage/data';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import ChartCard, { CardViz } from './chart-card';
import {
  otherPinnedViewsOf,
  pinnedChartIdFor,
  pinnedState,
} from './pinned-charts';
import QuestionScoringPanel from './question-scoring-panel';
import HighlightsPanel from './highlights-panel';
import ItemRankingPanel from './item-ranking-panel';
import { MeasureKind } from './chart-spec-builder';
import {
  BreakdownKind,
  breakdownQuestionKey,
  buildSpec,
  canBeBreakdown,
  fieldKey,
  QUESTION_BREAKDOWN_PREFIX,
  ChartableField,
  defaultVizFor,
  isPlottable,
} from './chart-spec-builder';

type FormVersionLite = {
  id: string;
  templateId: string;
  name: string;
  isAnonymous: boolean;
  createdAt: string;
};

/**
 * The quick options. `custom` has no range of its own — it reveals the picker,
 * and the dates chosen there become an `absolute` range.
 *
 * `thisMonth` and `lastYear` were already supported end to end and simply
 * never offered, so they are here too.
 */
export const CUSTOM_RANGE = 'custom';

/** The dashboard page, opened on the given dashboard when there is one. */
export const dashboardPath = (id?: string | null) =>
  id
    ? `../../dashboard?dashboard=${encodeURIComponent(id)}`
    : '../../dashboard';

/** Long enough that flipping through controls is one save, not twenty. */
const PREFERENCE_SAVE_DEBOUNCE_MS = 1000;

export const RANGES: { value: string; label: string; range: DateRangeSpec }[] =
  [
    { value: 'all', label: 'All time', range: { mode: 'allTime' } },
    {
      value: '30',
      label: 'Last 30 days',
      range: { mode: 'rolling', lastDays: 30 },
    },
    {
      value: '90',
      label: 'Last 90 days',
      range: { mode: 'rolling', lastDays: 90 },
    },
    {
      value: 'month',
      label: 'This month',
      range: { mode: 'preset', preset: 'thisMonth' },
    },
    {
      value: 'ytd',
      label: 'Year to date',
      range: { mode: 'preset', preset: 'yearToDate' },
    },
    {
      value: 'lastYear',
      label: 'Last year',
      range: { mode: 'preset', preset: 'lastYear' },
    },
  ];

const BREAKDOWNS: { value: BreakdownKind; label: string }[] = [
  { value: 'none', label: 'No breakdown' },
  { value: 'department', label: 'By department' },
  { value: 'month', label: 'By month' },
  { value: 'version', label: 'By survey version' },
];

/** The settings that belong to one survey, as stored. */
type TemplateSettings = {
  breakdown?: BreakdownKind;
  vizByField?: Record<string, CardViz>;
  percentByField?: Record<string, boolean>;
  measureByField?: Record<string, MeasureKind>;
  segmentLevel?: number | null;
};

/**
 * Which selector option a stored range corresponds to.
 *
 * The range is stored as a DateRangeSpec rather than as the selector's own
 * value, so it stays meaningful on its own — but the selector has to be put
 * back where the reader left it, which means matching one to the other.
 */
export const rangeValueFor = (spec: DateRangeSpec | null | undefined) => {
  if (!spec) return null;
  if (spec.mode === 'absolute') return CUSTOM_RANGE;
  const match = RANGES.find(
    (r) => JSON.stringify(r.range) === JSON.stringify(spec)
  );
  return match?.value ?? null;
};

/**
 * Stored settings are not trustworthy: a survey can lose a question, or be
 * deleted outright, between one visit and the next. Everything read back is
 * shape-checked and anything unrecognised is dropped rather than thrown on.
 */
export const asRecord = <T,>(value: unknown): Record<string, T> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, T>)
    : {};

/**
 * Survey results, one chart per question.
 *
 * Opens with every question already charted rather than on an empty canvas:
 * the first action is "make that a pie", not "configure a chart from scratch".
 */
export function ReportBuilderPage() {
  const { t } = useTranslation(['common']);
  const [templateId, setTemplateId] = useState<string>('');
  const [rangeValue, setRangeValue] = useState('all');
  /**
   * A comparison is a property of the window, not of the survey, so it stays
   * put when the template changes — the same reasoning that keeps the period
   * across templates.
   */
  const [compareTo, setCompareTo] = useState<CompareMode | null>(null);
  // Only meaningful while rangeValue is CUSTOM_RANGE. Kept as bare
  // yyyy-MM-dd; see the note on DateRangePicker about why not an instant.
  const [customRange, setCustomRange] = useState({
    startDate: '',
    endDate: '',
  });
  /**
   * Settings for every survey except the one on screen, whose settings live in
   * the state maps above. Switching survey moves the live ones in here and
   * takes the incoming survey's out — which is what makes a survey keep its
   * own setup instead of inheriting the last one's.
   */
  const [byTemplate, setByTemplate] = useState<
    Record<string, TemplateSettings>
  >({});
  // Nothing is saved until the stored settings have been read and applied.
  // Without this the first render would immediately save its own defaults over
  // whatever was there.
  const hydrated = useRef(false);
  const [breakdown, setBreakdown] = useState<BreakdownKind>('none');
  // Keyed by fieldKey (questionId, falling back to questionKey), never by
  // label. Two questions can share wording — across pages, across sections,
  // across versions of a reworded survey — and keying by text made them share
  // one entry, so changing one card silently changed the other card's spec
  // and pinning either stored the wrong configuration.
  const [vizByField, setVizByField] = useState<Record<string, CardViz>>({});
  // null means "let the server choose", which is not the same as level 0.
  const [segmentLevel, setSegmentLevel] = useState<number | null>(null);
  const [percentByField, setPercentByField] = useState<Record<string, boolean>>(
    {}
  );
  const [measureByField, setMeasureByField] = useState<
    Record<string, MeasureKind>
  >({});

  const { data: versionsData, loading: versionsLoading } = useQuery(
    GET_ALL_FORM_VERSIONS_FOR_CURRENT_USER,
    { fetchPolicy: 'cache-first' }
  );

  // Every edit mints a new version, so reporting on one version cuts the
  // history off at the last edit. Group them and report on the survey.
  const templates = useMemo(() => {
    const versions: FormVersionLite[] = versionsData?.formVersions ?? [];
    const byTemplateId = new Map<string, FormVersionLite[]>();
    versions.forEach((version) => {
      if (!version.templateId) return;
      byTemplateId.set(version.templateId, [
        ...(byTemplateId.get(version.templateId) ?? []),
        version,
      ]);
    });
    return [...byTemplateId.entries()]
      .map(([id, group]) => {
        const newestFirst = [...group].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        return { templateId: id, name: newestFirst[0]?.name ?? id };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [versionsData]);

  const selectedTemplateId = templateId || templates[0]?.templateId || '';

  const { data: fieldsData, loading: fieldsLoading } = useQuery(
    GET_CHARTABLE_FIELDS,
    {
      variables: { templateId: selectedTemplateId },
      skip: !selectedTemplateId,
      fetchPolicy: 'cache-first',
    }
  );

  const fields: ChartableField[] = useMemo(
    () => (fieldsData?.chartableFields ?? []).filter(isPlottable),
    [fieldsData]
  );

  /** The live settings, as they would be stored. */
  const currentSettings = (): TemplateSettings => ({
    breakdown,
    vizByField,
    percentByField,
    measureByField,
    segmentLevel,
  });

  /** Puts a survey's stored settings back on screen, tolerating rubbish. */
  const applySettings = (settings: TemplateSettings) => {
    setBreakdown((settings.breakdown as BreakdownKind) ?? 'none');
    setVizByField(asRecord<CardViz>(settings.vizByField));
    setPercentByField(asRecord<boolean>(settings.percentByField));
    setMeasureByField(asRecord<MeasureKind>(settings.measureByField));
    setSegmentLevel(
      typeof settings.segmentLevel === 'number' ? settings.segmentLevel : null
    );
  };

  /**
   * Switching survey files the outgoing one's settings and unpacks the
   * incoming one's. Doing both in one move is what fixes the two halves of
   * the same complaint: settings were neither kept for the survey you left
   * nor cleared from the one you arrived at.
   */
  const switchTemplate = (nextTemplateId: string) => {
    if (selectedTemplateId) {
      setByTemplate((prev) => ({
        ...prev,
        [selectedTemplateId]: currentSettings(),
      }));
    }
    applySettings(byTemplate[nextTemplateId] ?? {});
    setTemplateId(nextTemplateId);
  };

  const breakdownQuestions = useMemo(
    () => fields.filter(canBeBreakdown),
    [fields]
  );

  // Switching survey can strip the question the breakdown named, which would
  // otherwise leave the selector pointing at nothing and every chart silently
  // unbroken-down.
  const activeBreakdown: BreakdownKind =
    breakdownQuestionKey(breakdown) &&
    !breakdownQuestions.some(
      (f) => fieldKey(f) === breakdownQuestionKey(breakdown)
    )
      ? 'none'
      : breakdown;

  // Memoised on the primitives. The canned ranges are stable objects from the
  // module-level array, but a custom one is built here — and a fresh object
  // every render would rebuild every spec every render, which Apollo sees as
  // new variables and refetches for.
  const dateRange = useMemo<DateRangeSpec>(() => {
    if (rangeValue !== CUSTOM_RANGE) {
      return (
        RANGES.find((r) => r.value === rangeValue)?.range ?? RANGES[0].range
      );
    }
    // An incomplete custom range would query for nothing, so it falls back to
    // all time until both ends are filled in.
    return isCompleteRange(customRange.startDate, customRange.endDate)
      ? {
          mode: 'absolute',
          startDate: customRange.startDate,
          endDate: customRange.endDate,
        }
      : { mode: 'allTime' };
  }, [rangeValue, customRange.startDate, customRange.endDate]);

  const specs = useMemo(
    () =>
      fields.map((field) =>
        buildSpec(selectedTemplateId, field, {
          compareTo,
          dateRange,
          breakdown: activeBreakdown,
          fields,
          asPercent: !!percentByField[fieldKey(field)],
          measure: measureByField[fieldKey(field)] ?? 'distribution',
          // The same value the card renders from, so what is on screen is
          // what gets pinned.
          viz: vizByField[fieldKey(field)] ?? defaultVizFor(field),
        })
      ),
    [
      fields,
      selectedTemplateId,
      dateRange,
      activeBreakdown,
      compareTo,
      percentByField,
      measureByField,
      vizByField,
    ]
  );

  const { data: preferenceData } = useQuery(GET_MY_BUILDER_PREFERENCE, {
    fetchPolicy: 'cache-and-network',
  });
  const [savePreference] = useMutation(SAVE_BUILDER_PREFERENCE);

  // Put the reader back where they were, once.
  useEffect(() => {
    if (hydrated.current) return;
    const stored = preferenceData?.myBuilderPreference;
    if (!stored) return;
    hydrated.current = true;

    const storedValue = rangeValueFor(stored.dateRange);
    if (storedValue) {
      setRangeValue(storedValue);
      if (storedValue === CUSTOM_RANGE) {
        setCustomRange({
          startDate: stored.dateRange?.startDate ?? '',
          endDate: stored.dateRange?.endDate ?? '',
        });
      }
    }

    const templates = asRecord<TemplateSettings>(stored.byTemplate);
    setByTemplate(templates);

    // A survey that has since been deleted must not leave the page pointed at
    // nothing; the existing fallback to the first template covers it.
    if (stored.lastTemplateId) setTemplateId(stored.lastTemplateId);

    const mine = templates[stored.lastTemplateId ?? ''] ?? {};
    applySettings(mine);
  }, [preferenceData]);

  // Saved on a delay: these controls are flipped rapidly while exploring, and
  // a mutation per click would be traffic for nothing.
  useEffect(() => {
    if (!hydrated.current || !selectedTemplateId) return;
    const timer = setTimeout(() => {
      savePreference({
        variables: {
          input: {
            lastTemplateId: selectedTemplateId,
            dateRange,
            byTemplate: {
              ...byTemplate,
              [selectedTemplateId]: currentSettings(),
            },
          },
        },
      }).catch((error) =>
        // Losing a preference is not worth interrupting anyone over.
        console.error('Could not save builder preferences', error)
      );
    }, PREFERENCE_SAVE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // Deliberately the things worth saving, not everything the effect closes
    // over: currentSettings and savePreference are rebuilt every render, and
    // listing them would restart the timer on every render so it never fired.
  }, [
    selectedTemplateId,
    dateRange,
    byTemplate,
    breakdown,
    vizByField,
    percentByField,
    measureByField,
    segmentLevel,
  ]);

  // Scanned separately from the charts: this one asks "is anything different
  // between teams", which is a different question from "what did people say"
  // and carries its own multiple-comparison correction.
  const {
    data: highlightsData,
    loading: highlightsLoading,
    error: highlightsError,
  } = useQuery(GET_SURVEY_HIGHLIGHTS, {
    variables: {
      templateId: selectedTemplateId,
      dateRange,
      measure: 'topBox',
      // Omitted until the reader picks one: level 0 is the company itself, so
      // the server chooses the deepest level that can carry a comparison.
      ...(segmentLevel === null
        ? {}
        : {
            segment: {
              kind: 'department',
              level: segmentLevel,
              label: 'Department',
            },
          }),
    },
    skip: !selectedTemplateId,
    fetchPolicy: 'cache-and-network',
  });
  const highlights = highlightsData?.surveyHighlights;

  // Descriptive, and separate from the highlights scan: this one answers
  // "where are we weakest" with no comparison and no correction, because it
  // is not searching for anything.
  const { data: rankingData, loading: rankingLoading } = useQuery(
    GET_SURVEY_ITEM_RANKING,
    {
      variables: {
        templateId: selectedTemplateId,
        dateRange,
        measure: 'topBox',
      },
      skip: !selectedTemplateId,
      fetchPolicy: 'cache-and-network',
    }
  );
  const ranking = rankingData?.surveyItemRanking;

  // One call for every chart on the page: specs sharing a survey and window
  // are read once on the server rather than once per card.
  const {
    data: chartData,
    loading: chartsLoading,
    error: chartsError,
  } = useQuery(RUN_CHART_QUERIES, {
    variables: { specs },
    skip: !specs.length,
    fetchPolicy: 'cache-and-network',
  });

  const results: ChartResult[] = chartData?.runChartQueries ?? [];
  const anyTruncated = results.some((r) => r?.meta?.truncated);

  const [pinChart] = useMutation(PIN_CHART);
  const [unpinChart] = useMutation(UNPIN_CHART);

  /**
   * What is already on the dashboard being pinned to.
   *
   * Read from the server rather than remembered in a ref, so the pin state is
   * still right after a reload. It used to be session-only: a chart pinned
   * last week looked unpinned, and the button offered to pin it again.
   */
  const { data: dashboardData, refetch: refetchDashboard } = useQuery(
    GET_DASHBOARDS,
    { fetchPolicy: 'cache-and-network' }
  );

  /**
   * Which dashboard a pin goes to.
   *
   * The pin icons follow this: they show what is on the dashboard you are
   * pinning to, not on whichever one happens to be first.
   */
  // Kept in the URL, so "Add charts" on a dashboard can say which one it came
  // from and a chart pinned here lands there. An id that is not one of yours
  // falls back to the first dashboard, below.
  const [searchParams, setSearchParams] = useSearchParams();
  const pinTarget = searchParams.get('dashboard') ?? '';
  const setPinTarget = (id: string) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (id) params.set('dashboard', id);
        else params.delete('dashboard');
        return params;
      },
      { replace: true }
    );

  const dashboardOptions = useMemo(
    () => dashboardData?.dashboards ?? [],
    [dashboardData]
  );

  const activePinDashboard = useMemo(
    () =>
      dashboardOptions.find((d: any) => d.id === pinTarget) ??
      dashboardOptions[0] ??
      null,
    [dashboardOptions, pinTarget]
  );
  const { data: savedChartsData, refetch: refetchSavedCharts } = useQuery(
    GET_SAVED_CHARTS,
    { fetchPolicy: 'cache-and-network' }
  );

  const pinned = useMemo(
    () =>
      pinnedState(
        activePinDashboard?.tiles ?? [],
        savedChartsData?.savedCharts ?? []
      ),
    [activePinDashboard, savedChartsData]
  );
  const [pinning, setPinning] = useState<string | null>(null);
  const [scoringLabel, setScoringLabel] = useState<string | null>(null);

  /**
   * Saving the chart and pinning it are one action from the user's side, so
   * they are one handler: a chart nobody pinned is just clutter in the library.
   */
  // Pinning used to be silent in every outcome — no confirmation, and a
  // failure that reset the button and said nothing. One alert per outcome is
  // the idiom the rest of the app uses.
  const {
    fireAlert: firePinnedAlert,
    Alert: PinnedAlert,
    setAlertMessage: setPinnedMessage,
  } = useAlert({
    type: 'success',
    title: t('Pinned to your dashboard'),
    message: (
      <span className="inline-flex items-center gap-1">
        {t('It has been added to the bottom of your dashboard.')}
        {/* The link variant carries mx-4 my-2, which is wrong inline in a
            toast; className goes through twMerge so it wins. */}
        <Button to="../../dashboard" variant="link" className="mx-0 my-0">
          {t('View dashboard')}
        </Button>
      </span>
    ),
  });

  const { fireAlert: fireUnpinnedAlert, Alert: UnpinnedAlert } = useAlert({
    type: 'info',
    title: t('Removed from your dashboard'),
    message: t('The chart is no longer pinned.'),
  });

  const {
    fireAlert: firePinFailedAlert,
    Alert: PinFailedAlert,
    setAlertMessage: setPinFailedMessage,
  } = useAlert({
    type: 'error',
    title: t('Could not pin this chart'),
    message: t('Please try again.'),
  });

  /**
   * One handler, both directions.
   *
   * Saving the chart and placing it on the dashboard happen server-side in a
   * single call: the browser used to read the dashboard, append a tile and
   * write the whole array back, which raced anything else editing it.
   */
  const togglePin = useCallback(
    async (field: ChartableField, index: number) => {
      const key = fieldKey(field);
      // Matched on the exact spec this card would pin, so changing the chart
      // type or the breakdown gives you a new thing to pin rather than
      // silently re-pinning the old one.
      const alreadyPinned = pinnedChartIdFor(specs[index], pinned);
      setPinning(key);
      try {
        if (alreadyPinned) {
          await unpinChart({
            variables: {
              chartId: alreadyPinned,
              dashboardId: activePinDashboard?.id ?? null,
            },
          });
          await Promise.all([refetchDashboard(), refetchSavedCharts()]);
          fireUnpinnedAlert(4000);
          return;
        }

        await pinChart({
          variables: {
            input: {
              name: field.label,
              templateId: selectedTemplateId,
              spec: specs[index],
            },
            dashboardId: activePinDashboard?.id ?? null,
          },
        });
        await Promise.all([refetchDashboard(), refetchSavedCharts()]);
        // Name the dashboard: with more than one to choose from, "your
        // dashboard" stops being an answer.
        setPinnedMessage(
          <span className="inline-flex items-center gap-1">
            {t('Added to the bottom of {{name}}.', {
              name: activePinDashboard?.name ?? t('your dashboard'),
            })}
            <Button
              to={dashboardPath(activePinDashboard?.id)}
              variant="link"
              className="mx-0 my-0"
            >
              {t('View dashboard')}
            </Button>
          </span>
        );
        firePinnedAlert(6000);
      } catch (error: any) {
        console.error('Could not change what is pinned', error);
        setPinFailedMessage(error?.message ?? t('Please try again.'));
        firePinFailedAlert(8000);
      } finally {
        setPinning(null);
      }
    },
    [
      pinned,
      activePinDashboard,
      pinChart,
      unpinChart,
      refetchDashboard,
      refetchSavedCharts,
      selectedTemplateId,
      specs,
      firePinnedAlert,
      setPinnedMessage,
      fireUnpinnedAlert,
      firePinFailedAlert,
      setPinFailedMessage,
      t,
    ]
  );

  return (
    <div className="mt-10">
      <h1 className="mb-6 text-3xl font-bold text-gray-900 dark:text-gray-100">
        {t('Survey Results')}
      </h1>

      <Card className="mb-5">
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex min-w-[16rem] flex-1 flex-col text-sm">
            <span className="mb-1 font-medium">{t('Survey')}</span>
            <select
              className="rounded-md border-gray-300 text-sm dark:bg-dark-secondaryBackground"
              value={selectedTemplateId}
              onChange={(e) => switchTemplate(e.target.value)}
              disabled={versionsLoading || !templates.length}
            >
              {templates.map((template) => (
                <option key={template.templateId} value={template.templateId}>
                  {template.name}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col text-sm">
            <span className="mb-1 font-medium">{t('Period')}</span>
            <select
              className="rounded-md border-gray-300 text-sm dark:bg-dark-secondaryBackground"
              value={rangeValue}
              onChange={(e) => setRangeValue(e.target.value)}
            >
              {RANGES.map((range) => (
                <option key={range.value} value={range.value}>
                  {t(range.label)}
                </option>
              ))}
              <option value={CUSTOM_RANGE}>{t('Custom range…')}</option>
            </select>
          </label>

          {rangeValue === CUSTOM_RANGE && (
            <DateRangePicker
              className="min-w-[20rem]"
              startDate={customRange.startDate}
              endDate={customRange.endDate}
              onChange={setCustomRange}
              // The quick options above are the presets; a second row of them
              // would be two controls for one thing.
              showPresets={false}
            />
          )}

          <label className="flex flex-col text-sm">
            <span className="mb-1 font-medium">{t('Compare with')}</span>
            <select
              className="rounded-md border-gray-300 text-sm dark:bg-dark-secondaryBackground"
              value={compareTo ?? 'none'}
              onChange={(e) =>
                setCompareTo(
                  e.target.value === 'none'
                    ? null
                    : (e.target.value as CompareMode)
                )
              }
            >
              <option value="none">{t('No comparison')}</option>
              <option value="previousPeriod">{t('The previous period')}</option>
              <option value="previousYear">{t('A year earlier')}</option>
            </select>
          </label>

          <label className="flex flex-col text-sm">
            <span className="mb-1 font-medium">{t('Break down')}</span>
            <select
              className="rounded-md border-gray-300 text-sm dark:bg-dark-secondaryBackground"
              value={activeBreakdown}
              onChange={(e) => setBreakdown(e.target.value as BreakdownKind)}
            >
              {BREAKDOWNS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.label)}
                </option>
              ))}
              {/* Only scored single-choice questions: anything else has no
                  threshold to collapse at, and a column per option is a grid
                  of suppressed cells at these sample sizes. */}
              {!!breakdownQuestions.length && (
                <optgroup label={t('By another question')}>
                  {breakdownQuestions.map((field) => (
                    <option
                      key={fieldKey(field)}
                      value={`${QUESTION_BREAKDOWN_PREFIX}${fieldKey(field)}`}
                    >
                      {field.label}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>

          {dashboardOptions.length > 1 && (
            // Only worth showing once there is a choice to make. With one
            // dashboard, "pin" has an obvious destination and a select saying
            // so is just noise.
            <label className="flex min-w-[14rem] flex-col text-sm">
              <span className="mb-1 text-light-secondaryText">
                {t('Pin charts to')}
              </span>
              <select
                value={activePinDashboard?.id ?? ''}
                onChange={(e) => setPinTarget(e.target.value)}
                className="rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                {dashboardOptions.map((dashboard: any) => (
                  <option key={dashboard.id} value={dashboard.id}>
                    {dashboard.name}
                    {dashboard.isOwner ? '' : ` — ${t('shared with you')}`}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        {anyTruncated && (
          // Never let a partial answer look like a complete one.
          <p className="mt-4 rounded bg-semantic-warning/20 p-2 text-sm">
            {t(
              'This period has more responses than can be charted at once. Narrow the dates, or export the data instead.'
            )}
          </p>
        )}
      </Card>

      {!versionsLoading && !templates.length && (
        <Card>{t('No surveys yet. Create one to see results here.')}</Card>
      )}

      {!fieldsLoading && selectedTemplateId && !fields.length && (
        <Card>
          {t(
            'This survey has no multiple-choice questions to chart. Written answers can be read in the data export.'
          )}
        </Card>
      )}

      {!!selectedTemplateId && (
        <HighlightsPanel
          className="mb-5"
          highlights={highlights?.highlights}
          disclosure={highlights?.disclosure}
          segmentLabel={highlights?.segmentLabel}
          segmentLevel={highlights?.segmentLevel}
          levels={highlights?.levels}
          onSegmentLevelChange={setSegmentLevel}
          measure={highlights?.measure}
          scoredQuestions={highlights?.scoredQuestions ?? 0}
          unscoredQuestions={highlights?.unscoredQuestions ?? 0}
          loading={highlightsLoading && !highlights}
          error={highlightsError ? t('Highlights could not be loaded.') : null}
        />
      )}

      {!!ranking?.items?.length && (
        <ItemRankingPanel
          className="mb-5"
          items={ranking.items}
          measure={ranking.measure}
          respondentsInScope={ranking.respondentsInScope ?? 0}
          unscoredQuestions={ranking.unscoredQuestions ?? 0}
          loading={rankingLoading && !ranking}
        />
      )}

      <div className="grid grid-cols-6 gap-5 lg:grid-cols-12">
        {fields.map((field, i) => (
          <ChartCard
            key={fieldKey(field)}
            field={field}
            result={results[i]}
            loading={chartsLoading && !results[i]}
            error={chartsError ? t('This chart could not be loaded.') : null}
            viz={vizByField[fieldKey(field)] ?? defaultVizFor(field)}
            onVizChange={(viz) =>
              setVizByField((prev) => ({ ...prev, [fieldKey(field)]: viz }))
            }
            asPercent={!!percentByField[fieldKey(field)]}
            onAsPercentChange={(asPercent) =>
              setPercentByField((prev) => ({
                ...prev,
                [fieldKey(field)]: asPercent,
              }))
            }
            onPin={() => togglePin(field, i)}
            pinned={!!pinnedChartIdFor(specs[i], pinned)}
            otherPinnedViews={otherPinnedViewsOf(specs[i], pinned)}
            pinning={pinning === fieldKey(field)}
            onScore={
              field.suggestedScale || field.isScored
                ? () =>
                    setScoringLabel((current) =>
                      current === fieldKey(field) ? null : fieldKey(field)
                    )
                : undefined
            }
            scoringOpen={scoringLabel === fieldKey(field)}
            measure={measureByField[fieldKey(field)] ?? 'distribution'}
            onMeasureChange={(next) =>
              setMeasureByField((prev) => ({
                ...prev,
                [fieldKey(field)]: next,
              }))
            }
          >
            {scoringLabel === fieldKey(field) && (
              <QuestionScoringPanel
                templateId={selectedTemplateId}
                field={field}
                onClose={() => setScoringLabel(null)}
              />
            )}
          </ChartCard>
        ))}
      </div>

      {/* Fixed to the bottom of the viewport by the Alert's own styles, so
          position in the tree does not matter — but it has to be inside the
          router context for the dashboard link to resolve. */}
      <PinnedAlert />
      <UnpinnedAlert />
      <PinFailedAlert />
    </div>
  );
}

export default ReportBuilderPage;
